
import React, { useRef, useState } from 'react';
import { Download, RefreshCw, Hexagon, Pickaxe, Mountain, Microscope, ScanEye, Flame, Sparkles, BookOpen, Split, AlertTriangle, X, Share2, Hammer, Search, ArrowRight, Aperture, Box, Copy, Check, Loader2 } from 'lucide-react';
import { RockAnalysis, Language } from '../types';
import { translations } from '../translations';
import { CaseDistiller } from './CaseDistiller';

declare global {
  interface Window { 
    html2canvas: (element: HTMLElement, options?: Record<string, unknown>) => Promise<HTMLCanvasElement>; 
    heic2any?: (options: { blob: Blob; toType: string; quality: number }) => Promise<Blob | Blob[]>;
  }
}

interface Props {
  data: RockAnalysis;
  image: string;
  onReset: () => void;
  lang: Language;
  adminKey?: string;
}

export const AnalysisResult: React.FC<Props> = ({ data, image, onReset, lang, adminKey }) => {
  const t = translations[lang];
  const reportRef = useRef<HTMLDivElement>(null);
  const reportId = `SM-${new Date().getFullYear()}${(new Date().getMonth()+1).toString().padStart(2,'0')}-${Math.floor(Math.random()*10000)}`;
  const [showPreview, setShowPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackData, setFeedbackData] = useState({ correctName: '', reason: '' });
  const [feedbackSuccess, setFeedbackSuccess] = useState(false);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const [showDistiller, setShowDistiller] = useState(false);

  // 辅助函数：专门为反馈图片进行二次压缩 (800px)
  const resizeFeedbackImage = (base64: string, maxWidth: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject("Canvas error");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.6)); // 降低质量以减小体积
      };
      img.onerror = () => reject("Image load error");
      img.src = base64;
    });
  };

  const closeFeedback = () => {
    setShowFeedback(false);
    setFeedbackSuccess(false);
    setFeedbackData({ correctName: '', reason: '' });
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isFeedbackSubmitting) return;

    setIsFeedbackSubmitting(true);
    try {
      // 1. 压缩图片用于反馈 (降低体积，提高手机提交速度)
      let optimizedImage = image;
      try {
        optimizedImage = await resizeFeedbackImage(image, 800);
      } catch (resizeErr) {
        console.warn("Feedback image resize failed, using original", resizeErr);
      }

      // 2. 保存原始反馈
      const feedbackPayload = {
        reportId,
        originalName: data.name,
        correctName: feedbackData.correctName,
        reason: feedbackData.reason,
        image: optimizedImage,
      };

      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackPayload)
      });

      if (!response.ok) throw new Error(await response.text());
      
      console.log("Expert Feedback Submitted via API");
      setFeedbackSuccess(true);
    } catch (error) {
      console.error("Failed to save feedback:", error);
      alert("提交反馈失败，请检查网络连接");
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const dataURLtoBlob = (dataurl: string) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const generateReportImage = async (): Promise<string | null> => {
    if (!reportRef.current || !window.html2canvas) {
      console.error("Missing reportRef or html2canvas");
      return null;
    }
    
    try {
      await new Promise(resolve => setTimeout(resolve, 600));
      
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      const canvas = await window.html2canvas(reportRef.current, {
        useCORS: true,
        scale: isIOS ? 1.8 : 2.2, // 保持高清晰度
        backgroundColor: '#fcfcfc',
        logging: false,
        allowTaint: false,
        imageTimeout: 20000,
        // 核心黑科技：强制 html2canvas 在 1200px 的虚拟窗口下渲染
        // 这会自动触发 CSS 中的 md: (桌面端) 媒体查询，从而得到完美的电脑版布局
        windowWidth: 1200,
        onclone: (clonedDoc: Document) => {
          const el = clonedDoc.getElementById('report-container');
          if (el) {
            // 确保容器在虚拟窗口中以桌面端样式呈现
            el.style.width = '900px';
            el.style.margin = '0 auto';
            el.style.padding = '60px';
            el.style.transform = 'none';
            el.style.animation = 'none';
            el.style.transition = 'none';
            el.style.backgroundImage = 'none';
            el.style.boxShadow = 'none';
            
            // 移除所有可能干扰截图的滤镜
            const allElements = el.getElementsByTagName('*');
            for (let i = 0; i < allElements.length; i++) {
              const item = allElements[i] as HTMLElement;
              item.style.filter = 'none';
              item.style.webkitFilter = 'none';
            }
          }
        }
      });
      return canvas.toDataURL('image/png');
    } catch (e) {
      console.error("Report capture failed details:", e);
      return null;
    }
  };

  const handleSmartShare = async () => {
    if (isGenerating) return;
    
    setIsGenerating(true);
    try {
      const base64Image = await generateReportImage();
      
      if (!base64Image) {
        setIsGenerating(false);
        alert("生成报告失败，请重试");
        return;
      }

      // 在某些移动端环境下，异步操作后再调用 share 可能失效
      // 因此我们优先生成 base64 展示预览，作为最稳健的方案
      setPreviewImage(base64Image);
      
      const safeName = data.name.replace(/[\\/*?:"<>|]/g, "_");
      const filename = `${t.appName}_${safeName}.png`;
      const blob = dataURLtoBlob(base64Image);
      const file = new File([blob], filename, { type: 'image/png' });

      // 检查是否可以使用系统原生分享
      // 注意：在某些浏览器（如 iOS Chrome 或 iframe 环境）中，navigator.share 可能存在但无法分享文件
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ 
            files: [file], 
            title: t.reportTitle, 
            text: `${t.appName} - ${data.name}` 
          });
          setIsGenerating(false);
          return; // 分享成功，无需弹出预览
        } catch (shareErr) {
          console.log("Navigator share failed:", shareErr);
          // 失败则继续走预览逻辑
        }
      }
      
      // Fallback: 如果不支持原生分享或取消，则显示预览窗口供用户长按保存
      setShowPreview(true);
    } catch (error) {
      console.error("Smart share error:", error);
      alert("分享功能遇到错误，已为您开启预览模式");
      // 最终降级：强制显示预览
      if (previewImage) setShowPreview(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const isReverseMode = data.isThinSection;
  const crossRefTitle = isReverseMode ? t.microTitleThin : t.microTitleHand;
  const crossRefIcon = isReverseMode ? <Box size={14} /> : <Aperture size={14} />;
  const inferenceLabel = isReverseMode ? t.inferenceLabelThin : t.inferenceLabelHand;

  return (
    <div className="w-full max-w-5xl mx-auto animate-fade-in pb-10 md:pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-3 md:mb-6 px-2 md:px-4 gap-2 print:hidden">
        <button onClick={onReset} className="flex items-center gap-2 text-stone-600 hover:text-amber-700 transition-colors font-serif-sc w-full sm:w-auto justify-center text-xs md:text-sm py-2">
          <RefreshCw size={14} /><span>{t.reset}</span>
        </button>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={() => setShowFeedback(true)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white hover:bg-stone-50 text-stone-600 px-4 py-2 md:px-6 md:py-3 rounded-sm shadow-md transition-all border border-stone-200 font-serif-sc tracking-wide w-full text-xs md:text-sm">
            <AlertTriangle size={14} className="text-amber-600" />
            <span>{t.expertFeedback}</span>
          </button>
          <button onClick={handleSmartShare} disabled={isGenerating} className="flex-[2] sm:flex-none flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-900 text-amber-50 px-4 py-2 md:px-6 md:py-3 rounded-sm shadow-lg transition-all hover:shadow-xl font-serif-sc tracking-wide border border-stone-700 w-full text-xs md:text-sm">
            {isGenerating ? <div className="animate-spin w-3 h-3 md:w-4 md:h-4 border-2 border-white/30 border-t-white rounded-full"></div> : <Share2 size={14} />}
            <span>{isGenerating ? t.generating : t.saveShare}</span>
          </button>
        </div>
      </div>

      {/* Expert Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-stone-900/80 backdrop-blur-sm animate-fade-in" onClick={closeFeedback}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-shake" onClick={_e => _e.stopPropagation()}>
            <div className="bg-stone-800 p-6 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Pickaxe className="text-amber-500" />
                <h3 className="font-serif-sc font-bold text-lg">{t.feedbackTitle}</h3>
              </div>
              <button onClick={closeFeedback}><X size={20} /></button>
            </div>
            
            <div className="p-8">
              {feedbackSuccess ? (
                <div className="text-center py-10 space-y-4 animate-fade-in">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                    <Check size={32} />
                  </div>
                  <p className="text-stone-800 font-serif-sc font-bold">{t.feedbackSuccess}</p>
                  <button 
                    onClick={() => { closeFeedback(); setShowDistiller(true); }}
                    className="flex items-center justify-center gap-2 mx-auto px-6 py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-all shadow-lg"
                  >
                    <Sparkles size={16} />
                    <span>{t.distillTitle}</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">{t.feedbackCorrectName}</label>
                    <input 
                      required
                      type="text" 
                      value={feedbackData.correctName}
                      onChange={e => setFeedbackData(prev => ({ ...prev, correctName: e.target.value }))}
                      className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg focus:border-amber-500 outline-none transition-all font-serif-sc"
                      placeholder="e.g. 辉长岩 (Gabbro)"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-stone-400 uppercase tracking-widest">{t.feedbackReason}</label>
                    <textarea 
                      required
                      rows={4}
                      value={feedbackData.reason}
                      onChange={e => setFeedbackData(prev => ({ ...prev, reason: e.target.value }))}
                      className="w-full p-3 bg-stone-50 border border-stone-200 rounded-lg focus:border-amber-500 outline-none transition-all font-serif-sc text-sm"
                      placeholder={t.feedbackReason}
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isFeedbackSubmitting || !feedbackData.correctName || !feedbackData.reason}
                    className="w-full py-4 bg-stone-800 text-amber-50 rounded-xl font-serif-sc font-bold shadow-lg hover:bg-stone-900 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isFeedbackSubmitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        <span>正在提交...</span>
                      </>
                    ) : (
                      <span>{t.feedbackSubmit}</span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      <div ref={reportRef} id="report-container" className="bg-[#fcfcfc] text-stone-800 relative shadow-xl mx-auto p-3 md:p-[60px] h-auto print:shadow-none" style={{ width: '100%', maxWidth: '900px', backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")` }}>
        <div className="absolute inset-1 md:inset-6 border-2 md:border-4 border-double border-stone-300 pointer-events-none z-10"></div>
        <div className="absolute inset-2 md:inset-8 border border-stone-200 pointer-events-none z-10"></div>
        
        <div className="flex justify-between items-end border-b border-stone-800 pb-2 md:pb-6 mb-2 md:mb-8 relative z-20">
          <div>
             <div className="flex items-center gap-1.5 md:gap-3 text-stone-800 mb-0.5 md:mb-2"><Mountain className="w-3 h-3 md:w-8 md:h-8" /><span className="text-[8px] md:text-sm tracking-[0.15em] md:tracking-[0.3em] uppercase font-serif-sc">{t.appSub}</span></div>
             <h1 className="text-base md:text-5xl font-serif-sc font-bold text-stone-900 tracking-tight leading-none">{t.reportTitle}</h1>
             <p className="text-stone-500 text-[7px] md:text-sm mt-0.5 md:mt-2 tracking-widest font-serif-sc scale-90 origin-left md:scale-100 uppercase">Geological Identification Certificate</p>
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-xs text-stone-400 uppercase tracking-widest mb-1">{t.reportNo}</div>
            <div className="text-xl font-mono text-amber-700 font-bold">{reportId}</div>
            <div className="text-xs text-stone-400 mt-1">{new Date().toLocaleDateString()}</div>
          </div>
        </div>

        <div className="flex flex-col gap-2 md:gap-8 relative z-20">
          <div className="flex flex-col md:flex-row gap-2 md:gap-8 items-start">
             <div className="w-full md:w-5/12 shrink-0">
                <div className="relative h-32 md:h-auto md:aspect-[4/5] bg-stone-100 border-2 md:border-4 border-stone-200 shadow-inner p-1 md:p-2">
                   <div className="w-full h-full overflow-hidden relative">
                      <img src={image} alt="Sample" className="w-full h-full object-cover grayscale-[10%]" />
                   </div>
                </div>
                <div className="mt-0.5 md:mt-2 flex justify-center"><span className="bg-stone-800 text-amber-50 text-[6px] md:text-xs px-1.5 py-0.5 md:px-3 md:py-1 uppercase tracking-widest">{isReverseMode ? "Input Micrograph" : "Original Sample"}</span></div>
             </div>
             <div className="w-full md:w-7/12 flex flex-col pt-0 md:pt-2">
                <div>
                  <h2 className="text-[8px] md:text-sm font-bold text-stone-400 uppercase tracking-widest mb-0.5 md:mb-2">{t.resultTitle}</h2>
                  <div className="text-lg md:text-4xl font-serif-sc font-bold text-stone-900 mb-1 pb-1 md:mb-2 md:pb-4 border-b border-stone-200 leading-tight flex justify-between items-end">
                    <span>{data.name}</span>
                    <div className="flex flex-col items-end">
                      <span className="text-[8px] md:text-xs text-stone-400 uppercase tracking-widest mb-0.5">{t.confidence}</span>
                      <span className={`text-xs md:text-xl font-mono font-bold ${data.confidenceScore >= 90 ? 'text-emerald-600' : data.confidenceScore >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {data.confidenceScore}%
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-1 md:gap-6 mt-1 md:mt-6">
                    <div className="flex flex-col"><span className="text-[8px] md:text-xs text-stone-500 uppercase tracking-widest mb-0.5">{t.category}</span><span className="text-xs md:text-lg font-serif-sc text-stone-800 font-medium">{data.category}</span></div>
                    <div className="flex flex-col mt-1 md:mt-0"><span className="text-[8px] md:text-xs text-stone-500 uppercase tracking-widest mb-0.5">{t.interpretation}</span><p className="text-[10px] md:text-sm text-stone-700 leading-snug font-serif-sc text-justify">{data.formationReason}</p></div>
                  </div>
                </div>
                {data.geologicalContext && (
                  <div className="mt-1.5 md:mt-6 p-1.5 md:p-4 bg-amber-50/50 border border-amber-100 rounded-sm">
                     <div className="flex items-center gap-1 md:gap-2 mb-0.5 md:mb-2 text-amber-800"><BookOpen size={10} /><span className="font-bold text-[8px] md:text-xs uppercase tracking-wider">{t.geoContext}</span></div>
                     <p className="text-[10px] md:text-xs text-amber-900/80 leading-snug font-serif-sc italic text-justify">{data.geologicalContext}</p>
                  </div>
                )}
                {data.secondPossibility && data.secondPossibility.name && (
                   <div className="mt-1.5 md:mt-4 p-1.5 md:p-4 bg-stone-100 border-l-2 border-stone-400 rounded-sm">
                     <div className="flex items-center gap-1 md:gap-2 mb-0.5 text-stone-600"><Split size={10} /><span className="font-bold text-[8px] md:text-xs uppercase tracking-wider">{t.alternative}</span></div>
                     <div className="text-xs md:text-lg font-serif-sc font-bold text-stone-800 mb-0.5">{data.secondPossibility.name}</div>
                     <p className="text-[10px] md:text-xs text-stone-600 leading-snug font-serif-sc text-justify"><span className="font-bold mr-1">{t.diff}:</span>{data.secondPossibility.reason}</p>
                  </div>
                )}
             </div>
          </div>

          <div className="mt-0 md:mt-2">
             <div className="flex items-center gap-2 md:gap-4 mb-1 md:mb-4"><div className="h-px bg-stone-300 flex-grow"></div><h3 className="text-center font-bold text-stone-400 uppercase tracking-widest text-[8px] md:text-sm">{t.techData}</h3><div className="h-px bg-stone-300 flex-grow"></div></div>
             <div className="grid grid-cols-2 gap-x-2 md:gap-x-12 gap-y-2 md:gap-y-6">
                <div className="space-y-1 md:space-y-5">
                   <SpecItem icon={<Microscope size={14} />} label={t.grain} value={data.grainSize} />
                   <SpecItem icon={<Hexagon size={14} />} label={t.structure} value={data.textureAndStructure} />
                   <SpecItem icon={<Pickaxe size={14} />} label={t.hardness} value={data.hardness} />
                   <SpecItem icon={<ScanEye size={14} />} label={t.weathering} value={data.weathering} />
                </div>
                <div className="space-y-1 md:space-y-5">
                   <div>
                      <div className="flex items-center gap-1 md:gap-2 text-stone-500 mb-0.5 md:mb-1"><Hexagon className="text-amber-600 w-2.5 h-2.5 md:w-[18px] md:h-[18px]" /><span className="text-[8px] md:text-xs font-bold uppercase tracking-wider">{t.minerals}</span></div>
                      <div className="flex flex-wrap gap-1 md:gap-2 mt-0.5">{data.minerals.map((m, i) => (<span key={i} className="text-[8px] md:text-xs font-serif-sc border border-stone-300 px-1 md:px-2 py-0 md:py-0.5 text-stone-700 bg-white leading-none">{m}</span>))}</div>
                   </div>
                   <SpecItem icon={<Flame size={14} />} label={t.metamorphism} value={data.metamorphism} />
                   <SpecItem icon={<Sparkles size={14} />} label={t.colorOrigin} value={data.colorCause} />
                   <SpecItem icon={<Split size={14} />} label={t.alteration} value={data.alteration} />
                </div>
             </div>
          </div>

          {data.economicAnalysis && (
            <div className="mt-2 md:mt-6 bg-stone-50 border-2 border-dashed border-stone-300 p-2 md:p-6 relative">
               <div className="absolute -top-3 left-4 bg-[#fcfcfc] px-2 flex items-center gap-2 text-stone-500 font-bold text-[10px] md:text-sm uppercase tracking-widest"><Hammer size={14} />{t.economicTitle}</div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-8 mt-1 md:mt-2">
                 <div className="space-y-2 md:space-y-4">
                    <div><h4 className="flex items-center gap-1.5 text-[8px] md:text-xs font-bold text-stone-400 uppercase tracking-wider mb-0.5 md:mb-1"><Search size={10} className="text-emerald-600" />{t.alterationMap}</h4><p className="text-[10px] md:text-sm text-stone-800 font-serif-sc leading-tight text-justify">{data.economicAnalysis.alterationMapping}</p></div>
                    <div><h4 className="flex items-center gap-1.5 text-[8px] md:text-xs font-bold text-stone-400 uppercase tracking-wider mb-0.5 md:mb-1"><Sparkles size={10} className="text-emerald-600" />{t.indicators}</h4><p className="text-[10px] md:text-sm text-stone-800 font-serif-sc leading-tight text-justify">{data.economicAnalysis.mineralIndicators}</p></div>
                 </div>
                 <div className="space-y-2 md:space-y-4">
                    <div><h4 className="flex items-center gap-1.5 text-[8px] md:text-xs font-bold text-stone-400 uppercase tracking-wider mb-0.5 md:mb-1"><ArrowRight size={10} className="text-emerald-600" />{t.structuralControl}</h4><p className="text-[10px] md:text-sm text-stone-800 font-serif-sc leading-tight text-justify">{data.economicAnalysis.structuralControl}</p></div>
                    <div><h4 className="flex items-center gap-1.5 text-[8px] md:text-xs font-bold text-stone-400 uppercase tracking-wider mb-0.5 md:mb-1"><Hammer size={10} className="text-emerald-600" />{t.potential}</h4><p className="text-[10px] md:text-sm text-emerald-900 font-bold font-serif-sc leading-tight text-justify bg-emerald-50/50 p-1 md:p-2 rounded border border-emerald-100/50">{data.economicAnalysis.prospectingPotential}</p></div>
                 </div>
               </div>
            </div>
          )}

          {data.fossilAnalysis && (
            <div className="mt-2 md:mt-6 bg-emerald-50/30 border-2 border-emerald-200 p-2 md:p-6 relative rounded-sm">
               <div className="absolute -top-3 left-4 bg-[#fcfcfc] px-2 flex items-center gap-2 text-emerald-700 font-bold text-[10px] md:text-sm uppercase tracking-widest"><Mountain size={14} />{t.fossilTitle}</div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-8 mt-1 md:mt-2">
                 <div className="space-y-2 md:space-y-4">
                    <div className="flex flex-col border-b border-emerald-100 pb-1 md:pb-2">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.fossilType}</span>
                      <span className="text-[10px] md:text-sm text-stone-800 font-serif-sc font-medium">{data.fossilAnalysis.fossilType}</span>
                    </div>
                    <div className="flex flex-col border-b border-emerald-100 pb-1 md:pb-2">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.genusSpecies}</span>
                      <span className="text-[10px] md:text-sm text-stone-900 font-serif-sc font-bold italic">{data.fossilAnalysis.genusSpecies}</span>
                    </div>
                    <div className="flex flex-col border-b border-emerald-100 pb-1 md:pb-2">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.geologicalAge}</span>
                      <span className="text-[10px] md:text-sm text-stone-800 font-serif-sc font-medium">{data.fossilAnalysis.geologicalAge}</span>
                    </div>
                 </div>
                 <div className="space-y-2 md:space-y-4">
                    <div className="flex flex-col border-b border-emerald-100 pb-1 md:pb-2">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.preservation}</span>
                      <span className="text-[10px] md:text-sm text-stone-800 font-serif-sc font-medium">{data.fossilAnalysis.preservation}</span>
                    </div>
                    <div className="flex flex-col border-b border-emerald-100 pb-1 md:pb-2">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.environment}</span>
                      <span className="text-[10px] md:text-sm text-stone-800 font-serif-sc font-medium">{data.fossilAnalysis.environment}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[8px] md:text-xs text-emerald-600 uppercase tracking-widest mb-0.5 font-bold">{t.fossilDesc}</span>
                      <p className="text-[10px] md:text-xs text-stone-700 font-serif-sc leading-tight text-justify italic">"{data.fossilAnalysis.description}"</p>
                    </div>
                 </div>
               </div>
            </div>
          )}

          {data.microscopicAnalysis && (
            <div className={`mt-2 md:mt-6 text-stone-300 p-2 md:p-6 relative border border-stone-700 shadow-inner ${isReverseMode ? 'bg-[#2a2725]' : 'bg-[#1a1c20]'}`}>
               <div className={`absolute -top-3 left-4 px-2 flex items-center gap-2 font-bold text-[10px] md:text-sm uppercase tracking-widest border border-stone-600 ${isReverseMode ? 'bg-stone-700 text-emerald-300' : 'bg-stone-800 text-purple-300'}`}>{crossRefIcon}{crossRefTitle}</div>
               <div className="flex flex-col gap-4 md:gap-6 mt-2 md:mt-4">
                  <div className="w-full space-y-3 md:space-y-6">
                      <div><h4 className={`flex items-center gap-1.5 text-[8px] md:text-xs font-bold uppercase tracking-wider mb-0.5 md:mb-1 ${isReverseMode ? 'text-emerald-500' : 'text-purple-400'}`}><Split size={10} />{inferenceLabel}</h4><p className={`text-[10px] md:text-sm text-stone-300 font-serif-sc leading-tight text-justify italic border-l-2 pl-2 ${isReverseMode ? 'border-emerald-500' : 'border-purple-500'}`}>"{data.microscopicAnalysis.inference}"</p></div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
                         <div>
                           <h4 className="text-[8px] md:text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">
                             {isReverseMode ? t.macroStructure : "XPL"}
                           </h4>
                           <p className="text-[10px] md:text-xs text-stone-400 font-mono leading-tight">
                             {data.microscopicAnalysis.xplFeatures}
                           </p>
                         </div>
                         <div>
                           <h4 className="text-[8px] md:text-xs font-bold text-stone-500 uppercase tracking-wider mb-1">
                             {isReverseMode ? t.macroAppearance : "PPL"}
                           </h4>
                           <p className="text-[10px] md:text-xs text-stone-400 font-mono leading-tight">
                             {data.microscopicAnalysis.pplFeatures}
                           </p>
                         </div>
                      </div>
                  </div>
               </div>
            </div>
          )}
          <div className="mt-1 md:mt-4 border-t border-stone-200 pt-1 md:pt-4"><h3 className="font-bold text-stone-400 uppercase tracking-widest text-[8px] md:text-sm mb-0.5 md:mb-2">{t.expertEval}</h3><p className="text-[10px] md:text-sm text-stone-800 leading-snug font-serif-sc text-justify whitespace-pre-line">{data.detailedDescription}</p></div>
          <div className="mt-2 md:mt-8 pt-2 md:pt-4 border-t-2 border-double border-stone-300 flex justify-between items-end">
            <div className="flex flex-col gap-1 md:gap-2">
              <div className="text-[6px] md:text-[10px] text-stone-400 uppercase tracking-widest">AI Geological Analysis • Research Only</div>
              <div className="text-[6px] md:text-[10px] text-red-800 font-serif-sc flex items-center gap-2 md:gap-4">
                <span className="font-medium">www.stonemaster.ai</span>
                <span className="w-px h-2 md:h-3 bg-stone-300"></span>
                <span>关注小红书：石头大人</span>
              </div>
            </div>
            <div className="relative">
              <div className="w-12 h-12 md:w-24 md:h-24 border-2 md:border-4 border-red-800 rounded-full flex items-center justify-center opacity-80 transform rotate-[-12deg]">
                <div className="text-[6px] md:text-xs text-red-800 font-bold uppercase tracking-widest text-center leading-tight">{t.appName}<br/>{t.verified}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPreview && previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-stone-900/95 backdrop-blur-sm animate-fade-in" onClick={() => setShowPreview(false)}>
          <div className="relative w-full max-w-lg bg-stone-800 rounded-xl overflow-hidden shadow-2xl flex flex-col h-[85vh] md:h-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b border-stone-700 bg-stone-800 sticky top-0 z-10"><h3 className="text-white font-serif-sc font-bold flex items-center gap-2 text-sm md:text-base"><Share2 size={18} className="text-emerald-500" />{t.saveShare}</h3><button onClick={() => setShowPreview(false)} className="text-stone-400 hover:text-white p-1"><X size={24} /></button></div>
            <div className="flex-1 overflow-y-auto p-4 bg-stone-900/50 flex flex-col items-center">
              <div className="w-full bg-emerald-900/30 border border-emerald-800 rounded-lg p-3 mb-4 flex items-start gap-3"><div className="bg-emerald-800 rounded-full p-1.5 shrink-0 mt-0.5"><Download size={16} className="text-emerald-100" /></div><div className="text-left"><p className="text-emerald-100 text-xs font-bold mb-0.5">{t.saveTipTitle}</p><p className="text-emerald-200/70 text-[10px] leading-relaxed">{t.saveTipDesc}</p></div></div>
              <img src={previewImage} alt="Preview" className="w-full h-auto rounded shadow-lg border border-stone-600 mb-4 select-none touch-callout-default" style={{ WebkitTouchCallout: 'default' }} />
            </div>
            <div className="p-4 border-t border-stone-700 bg-stone-800 grid grid-cols-2 gap-3">
               <button onClick={async () => { try { const blob = dataURLtoBlob(previewImage); const item = new ClipboardItem({ 'image/png': blob }); await navigator.clipboard.write([item]); setCopySuccess(true); setTimeout(() => setCopySuccess(false), 2000); } catch { alert("Failed to copy"); } }} className={`flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all text-sm border ${copySuccess ? 'bg-emerald-700 border-emerald-600 text-white' : 'bg-stone-700 border-stone-600 text-stone-200 hover:bg-stone-600'}`}>
                 {copySuccess ? <Check size={16} /> : <Copy size={16} />}{copySuccess ? t.copied : t.copyImage}
               </button>
               <button onClick={() => { const link = document.createElement('a'); link.download = `${t.appName}_Report.png`; link.href = previewImage; link.click(); }} className="flex items-center justify-center gap-2 py-3 bg-stone-200 text-stone-900 rounded-lg font-medium hover:bg-white transition-colors text-sm"><Download size={16} />{t.downloadFile}</button>
            </div>
          </div>
        </div>
      )}

      {showDistiller && (
        <CaseDistiller 
          analysis={data}
          image={image}
          lang={lang}
          onClose={() => setShowDistiller(false)}
          adminKey={adminKey}
        />
      )}
    </div>
  );
};

const SpecItem: React.FC<{ icon: React.ReactNode, label: string; value: string }> = ({ icon, label, value }) => (
  <div className="flex flex-col border-b border-stone-200 pb-0.5 md:pb-2">
    <div className="flex items-center gap-1 md:gap-2 text-stone-500 mb-0.5 md:mb-1"><span className="text-amber-600">{icon}</span><span className="text-[8px] md:text-xs font-bold uppercase tracking-wider">{label}</span></div>
    <span className="text-[10px] md:text-sm text-stone-800 font-serif-sc pl-3.5 md:pl-6 leading-tight">{value}</span>
  </div>
);